/**
 * SortSelect — result ordering (STORY-003-005; restyled 2026-08).
 *
 * Sort modes: `relevance` (default → recency in browse mode), `price_asc`,
 * `price_desc`. STORY-003-009 added browse-mode price sorting (the backend
 * honours `sort=price_asc|price_desc` even with no `q`), so the price sorts are
 * functional on browse and the control stays enabled. `relevance` on browse
 * resolves server-side to `created_at DESC, id DESC`.
 *
 * Still a native `<select>`: `@radix-ui/react-select` is not a project
 * dependency, and a native select is fully accessible, keyboard-operable,
 * zero-bundle, and — the reason that matters here — it opens as the platform
 * picker on the mid-range Android this product is actually used on. It is
 * `appearance-none` over the sunken well with a drawn chevron so it matches the
 * price fields rather than rendering as an OS control in the middle of the slip.
 *
 * The label id was a module-level constant (`marketplace-sort-label`), which
 * duplicated in the DOM the moment the page rendered its two sidebar instances
 * (desktop column + mobile sheet) and left both selects pointing at an
 * ambiguous `aria-labelledby`. It is a `useId()` now.
 */
import { useId, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import { iconProps } from "@/lib/icons";

export type SortValue = "relevance" | "price_asc" | "price_desc";

export const SORT_VALUES: readonly SortValue[] = [
  "relevance",
  "price_asc",
  "price_desc",
];

export function isSortValue(value: string | null): value is SortValue {
  return value === "relevance" || value === "price_asc" || value === "price_desc";
}

export interface SortSelectProps {
  value: SortValue;
  onChange: (value: SortValue) => void;
}

export function SortSelect({ value, onChange }: SortSelectProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const labelId = useId();
  const selectId = useId();

  return (
    <div className="flex flex-col gap-1.5">
      {/* Visually hidden: the sidebar facet already prints this heading above
          the control, and printing it twice is exactly the duplication the
          shipped sidebar shipped with. AT still needs the select named. */}
      <label id={labelId} htmlFor={selectId} className="sr-only">
        {t("filters.sort")}
      </label>
      <div className="relative">
        <select
          id={selectId}
          aria-labelledby={labelId}
          data-testid="sort-select"
          value={value}
          onChange={(e) => {
            const next = e.target.value;
            if (isSortValue(next)) onChange(next);
          }}
          className="h-11 w-full appearance-none rounded-[10px] border border-[color:var(--g-rule)] bg-[color:var(--g-sunken)] px-3 pr-10 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {SORT_VALUES.map((sort) => (
            <option key={sort} value={sort}>
              {t(`sort.${sort}`)}
            </option>
          ))}
        </select>
        <ChevronDown
          {...iconProps("sm")}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--g-ink-2)]"
        />
      </div>
    </div>
  );
}
